import { Node } from 'react-flow-renderer';
export interface CommonTechnicalProfile {
  id: string;
  displayName: string;
  domain: string;
  description: string;
  protocol: [string, string];
  inputClaims: Array<InputClaim>;
  outputClaims: Array<OutputClaim>;
  inputClaimsTransformations: Array<string>;
  outputClaimsTransformations: Array<string>;
  metadata: Array<[string, string]>;
  claimProvider: string;
  includeTechnicalProfile: string;
}

export interface SelfAssertedProfile extends CommonTechnicalProfile {
  displayClaims: Array<DisplayClaim>;
  // Validation technical profile: name, continueOnError, continueOnSuccess, Preconditions
  validationTechnicalProfiles: Array<ValidationTP>;
}

export interface NonInteractiveTechnicalProfile extends CommonTechnicalProfile {
  persistedClaims: Array<PersistedClaim>;
}

export interface ClaimProvider {
  displayName: string;
  domain: string;
}

export enum PreconditionType {
  CLAIM_EXIST = 'ClaimsExist',
  CLAIM_EQUALS = 'ClaimEquals',
}

export enum PreconditionAction {
  SKIPVALIDATIONTECHINCALPROFILE = 'SkipThisValidationTechnicalProfile',
  SKIPORCHESTRATIONSTEP = 'SkipThisOrchestrationStep',
}
// Type: ClaimsExist, Execute action if value, claim to check, and the final value is the Action totake
// Type: ClaimsEquals, Execute action if value, claim to check, the value to compare against, and the final value is the Action to take
export interface Precondition {
  type: PreconditionType;
  executeActionsIf: boolean;
  values: Array<string>;
  action: PreconditionAction;
}

export interface Enumeration {
  value: string;
  text: string;
  selectByDefault: boolean;
}

export interface Pattern {
  regularExpression: string;
  helpText: string;
}

export interface Mask {
  type: string;
  regex: string;
  value: string;
}

export interface AADAttribute {
  id: string;
  dataType: string; // drop down , for now use string and boolean
}

export interface Claim {
  id: string;
  displayName: string;
  dataType: string; // drop down , for now use string and boolean
  userInputType: string; // drop down, for now use textbox and multiselect,
  restriction?: 'Enumeration' | 'Pattern'; // enumeration or pattern
  enumeration?: Enumeration[];
  pattern?: Pattern;
  userHelpText?: string;
  adminHelpText?: string;
  mask?: Mask;
  PredicateValidationReference?: any; // Not sure of what this does
  userCreated?: boolean;
}

export interface InputClaim {
  claimTypeReferenceId: string;
  partnerClaimType: string;
  defaultValue: string;
  required: boolean;
  alwaysUseDefaultValue: boolean;
}

export interface OutputClaim {
  claimTypeReferenceId: string;
  partnerClaimType: string;
  defaultValue: string;
  required: boolean;
  alwaysUseDefaultValue: boolean;
}

export interface DisplayClaim {
  claimTypeReferenceId: string;
  displayControlReferenceId: string;
  required: boolean;
}

export interface PersistedClaim {
  claimTypeReferenceId: string;
  partnerClaimType: string;
  defaultValue: string;
}

export interface Transformation {
  claimReferenceId: string;
  transformationClaimType: string;
}

export interface TransformationInputParams {
  id: string;
  dataType: string;
  value: string;
}

export interface ClaimsTransformation {
  id: string;
  transformationmethod: string;
  inputClaims: Array<Transformation>;
  inputParameters: Array<TransformationInputParams>;
  outputClaims: Array<Transformation>;
}

export interface UserJourney {
  id: string;
  defaultCpimIssuerTechnicalProfileReferenceId: string;
  authorizationTP: string;
  orchestrationSteps: Array<OrchestrationStep>;
}

export interface SubJourney {
  id: string;
  type: string;
  orchestrationSteps: Array<OrchestrationStep>;
}

export interface OrchestrationStep {
  order: number;
  type: string;
  contentDefinitonReferenceId: string;
  cpimIssuerTechnicalProfileReferenceId: string;
  preconditions: Array<Precondition>;
  claimsProviderSelections: ProviderSelections;
  claimsExchanges: Array<ClaimsExchange>;
  subJourneyToInvoke: string;
}

export interface ProviderSelections {
  displayOptions: string;
  providers: Array<ProviderSelect>;
}

export interface ProviderSelect {
  targetClaimExchangeId: string;
  validationClaimsExchange: string;
}

export interface ClaimsExchange {
  id: string;
  technicalProfilerefrenceId: string;
}

export interface ValidationTP {
  id: string;
  continueOnError: boolean;
  continueOnSuccess: boolean;
  preconditions: Array<Precondition>;
}

export interface PolicyComponents {
  basePolicy?: string;
  claims: Record<string, Claim>;
  claimProviders: Array<ClaimProvider>;
  claimTransformations: Record<string, ClaimsTransformation>;
  selfAssertedProfiles: Record<string, SelfAssertedProfile>;
  otherProfiles: Record<string, NonInteractiveTechnicalProfile>;
  userJourneys: Array<UserJourney>;
  subJourneys: Array<SubJourney>;
  rawXMLString: string;
}

export interface RPFileComponents extends PolicyComponents {
  defaultUserJourney: string;
  Endpoints?: UserInfoEndpoint[]; // the docs says there is only 1 endpoint. not sure if bad docs or it is true
  UserJourneyBehaviors?: string;
  TechnicalProfile: string;
}

export interface UserInfoEndpoint {
  Id: string;
  UserJourneyReferenceId: string;
}

export interface AllPolicyXML {
  policyComponents: Record<string, PolicyComponents>;
  rpComponents: Record<string, RPFileComponents>;
  rpInheritance: Record<string, Array<string>>;
}

export interface CustomNode extends Node {
  parentId: string;
}
